import React, {
    CSSProperties, FC, useEffect, useMemo, useRef, useState,
} from 'react';

import { Button } from 'antd';
import classNames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import appConfig, { IS_LOCAL_ENV } from '../../../config/env';
import Spinner from '../../Common/Spin';
import { useMounted, useStores } from '../../hooks';
import { AuthStates, PdfViewerSession } from '../../../types/types';
import { PdfViewerClient } from './client/pdfViewerClient';
import { isUnauthorized } from './client/errors';
import { MAX_DPR } from './constants';
import ViewerBody from './components/ViewerBody';
import useWatermarks from './hooks/useWatermarks';
import { PageInfo } from './types';
import styles from './PdfTileViewer.module.scss';

export interface PdfTileViewerPublicProps {
    setIsLoading: (value: boolean) => void;
    filePolicyId: string;
    carouselMode?: boolean;
    PDFViewerClassName?: string;
    PDFViewerStyle?: CSSProperties;
    onSessionExpired?: () => void;
    onRefreshSession?: () => Promise<PdfViewerSession>;
}

interface PdfTileViewerProps extends PdfTileViewerPublicProps {
    session: PdfViewerSession;
}

const preventDefault = (event: React.SyntheticEvent): void => event.preventDefault();

const PdfTileViewer: FC<PdfTileViewerProps> = observer(({
    session,
    filePolicyId,
    setIsLoading,
    carouselMode = false,
    PDFViewerClassName,
    PDFViewerStyle,
    onSessionExpired,
    onRefreshSession,
}) => {
    const { t } = useTranslation();
    const [pages, setPages] = useState<PageInfo[]>([]);
    const [isExpired, setIsExpired] = useState<boolean>(false);
    const [hasFailed, setHasFailed] = useState<boolean>(false);
    const isMountedRef = useMounted();
    const expiredHandlerRef = useRef<() => void>();
    expiredHandlerRef.current = () => setIsExpired(true);
    const refreshHandlerRef = useRef(onRefreshSession);
    refreshHandlerRef.current = onRefreshSession;

    const { watermarks, hasFailed: hasWatermarksFailed } = useWatermarks({
        filePolicyId,
        isMountedRef,
        setIsLoading,
    });

    const client = useMemo(() => new PdfViewerClient(session, {
        apiUrl: appConfig.API_URL,
        allowLoopbackHttp: IS_LOCAL_ENV,
        dpr: Math.min(window.devicePixelRatio || 1, MAX_DPR),
        refreshSession: onRefreshSession ? () => refreshHandlerRef.current() : undefined,
        onUnauthorized: () => expiredHandlerRef.current(),
    }), [session.serverUrl, session.documentId]);

    useEffect(() => () => client.dispose(), [client]);

    const { userStore } = useStores();
    useEffect(() => reaction(
        () => userStore.authState,
        (authState) => {
            if (authState !== AuthStates.authenticated) {
                client.dispose();
                setPages([]);
                setIsExpired(true);
            }
        },
    ), [client, userStore]);

    useEffect(() => {
        client.setSession(session);
        setIsExpired(false);
    }, [client, session.jwt]);

    useEffect(() => {
        if (pages.length) {
            return undefined;
        }
        let cancelled = false;
        setHasFailed(false);
        client.getInfo()
            .then((info) => !cancelled && setPages(info))
            .catch((error) => !cancelled && !isUnauthorized(error) && setHasFailed(true));
        return () => {
            cancelled = true;
        };
    }, [client, session.jwt]);

    const isReady = !!watermarks && pages.length > 0;

    return (
        <div
            className={classNames(styles.root, PDFViewerClassName)}
            style={PDFViewerStyle}
            onContextMenu={preventDefault}
            onDragStart={preventDefault}
        >
            {isReady && (
                <ViewerBody
                    key={session.documentId}
                    client={client}
                    pages={pages}
                    watermarks={watermarks}
                    carouselMode={carouselMode}
                />
            )}
            {!isReady && !hasFailed && !hasWatermarksFailed && !isExpired && (
                <div className={styles.loading}><Spinner spinning /></div>
            )}
            {(hasFailed || hasWatermarksFailed) && !isExpired && (
                <div className={styles.message}>{t('viewer.tileViewer.loadError')}</div>
            )}
            {isExpired && (
                <div className={styles.message}>
                    <span>{t('viewer.tileViewer.sessionExpired')}</span>
                    {onSessionExpired && (
                        <Button type="primary" onClick={onSessionExpired}>
                            {t('viewer.tileViewer.reload')}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
});

export default PdfTileViewer;
